import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import storage from '../helpers/storage';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';
import useDatabase from '../hooks/useDatabase';

export interface MoodTrackingState {
  enabled: boolean;
  shouldShowPopup: boolean;
  lastCheckTime: string | null;
  initialized: boolean;
  dwellThresholdSeconds: number;
}

const DEFAULT_DWELL_THRESHOLD_SECONDS = 20 * 60;
const SESSION_INACTIVITY_RESET_MS = 30 * 60 * 1000;
const MIN_TIME_BETWEEN_POPUPS_MS = 30 * 60 * 1000;

interface MoodTrackingContextValue {
  moodTrackingEnabled: boolean;
  shouldShowMoodPopup: boolean;
  lastCheckTime: string | null;
  initialized: boolean;
  enableMoodTracking: () => Promise<void>;
  disableMoodTracking: () => Promise<void>;
  hideMoodPopup: (moodSubmitted?: boolean) => Promise<void>;
  reportFactiiiEngagement: (
    factiiiId: number,
    durationSeconds: number
  ) => Promise<void>;
  resetSession: () => void;
}

const MoodTrackingContext = createContext<MoodTrackingContextValue | null>(
  null
);

export const useMoodTrackingContext = (): MoodTrackingContextValue => {
  const context = useContext(MoodTrackingContext);
  if (!context) {
    throw new Error(
      'useMoodTrackingContext must be used within a MoodTrackingProvider'
    );
  }
  return context;
};

export const MoodTrackingProvider: React.FC<{
  children: React.ReactNode;
  client?: WrappedTRPCClient;
}> = ({ children, client }) => {
  const [state, setState] = useState<MoodTrackingState>({
    enabled: false,
    shouldShowPopup: false,
    lastCheckTime: null,
    initialized: false,
    dwellThresholdSeconds: DEFAULT_DWELL_THRESHOLD_SECONDS
  });

  // Mutable session-only state — never triggers re-render, so refs are correct here.
  const sessionEngagementsRef = useRef<Map<number, number>>(new Map());
  // `null` until the first engagement, not `Date.now()`: a clock read during
  // render is refused by Cache Components once this provider sits in the
  // prerendered app shell (web), and the first engagement can't be "inactive".
  const lastActivityTimeRef = useRef<number | null>(null);

  const { getLatestMoodEntry, isReady } = useDatabase();

  const enableMoodTracking = async () => {
    try {
      setState((prev) => ({ ...prev, enabled: true }));
      await storage.setItem('moodTrackingEnabled', 'true');
    } catch (error) {
      console.error('Error enabling mood tracking:', error);
    }
  };

  const disableMoodTracking = async () => {
    try {
      setState((prev) => ({ ...prev, enabled: false, shouldShowPopup: false }));
      await storage.setItem('moodTrackingEnabled', 'false');
    } catch (error) {
      console.error('Error disabling mood tracking:', error);
    }
  };

  const resetSession = () => {
    sessionEngagementsRef.current.clear();
    lastActivityTimeRef.current = Date.now();
  };

  const hideMoodPopup = async (moodSubmitted = false) => {
    try {
      const now = new Date().toISOString();
      setState((prev) => ({
        ...prev,
        shouldShowPopup: false,
        lastCheckTime: now
      }));

      await storage.setItem('lastMoodCheckTime', now);
      await storage.setItem('moodTrackingIntroSeen', 'true');

      if (moodSubmitted) resetSession();
    } catch (error) {
      console.error('Error hiding mood popup:', error);
    }
  };

  const reportFactiiiEngagement = async (
    factiiiId: number,
    durationSeconds: number
  ) => {
    if (!state.enabled || state.shouldShowPopup) return;

    const now = Date.now();

    if (
      lastActivityTimeRef.current !== null &&
      now - lastActivityTimeRef.current > SESSION_INACTIVITY_RESET_MS
    ) {
      resetSession();
    }

    lastActivityTimeRef.current = now;

    const currentEngagement = sessionEngagementsRef.current.get(factiiiId) || 0;
    const newEngagement = currentEngagement + durationSeconds;
    sessionEngagementsRef.current.set(factiiiId, newEngagement);

    if (newEngagement < state.dwellThresholdSeconds) return;

    const lastCheck = await storage.getItem('lastMoodCheckTime');
    let lastPopupTime = lastCheck ? new Date(lastCheck).getTime() : 0;

    if (isReady) {
      try {
        const latestEntry = await getLatestMoodEntry();
        if (latestEntry?.timestamp) {
          const entryTime = new Date(latestEntry.timestamp).getTime();
          if (entryTime > lastPopupTime) lastPopupTime = entryTime;
        }
      } catch {
        // DB unavailable, rely on storage only
      }
    }

    if (lastPopupTime && now - lastPopupTime < MIN_TIME_BETWEEN_POPUPS_MS) {
      return;
    }

    sessionEngagementsRef.current.set(factiiiId, 0);
    setState((prev) => ({ ...prev, shouldShowPopup: true }));
  };

  useEffect(() => {
    let ignore = false;

    const initialize = async () => {
      try {
        const [storedEnabled, storedLastCheck, storedIntroSeen] =
          await Promise.all([
            storage.getItem('moodTrackingEnabled'),
            storage.getItem('lastMoodCheckTime'),
            storage.getItem('moodTrackingIntroSeen')
          ]);

        if (ignore) return;

        const enabled = storedEnabled === 'true';
        const introSeen = storedIntroSeen === 'true';

        let shouldShow = !introSeen;

        if (shouldShow && storedLastCheck) {
          const timeSince = Date.now() - new Date(storedLastCheck).getTime();
          if (timeSince < MIN_TIME_BETWEEN_POPUPS_MS) shouldShow = false;
        }

        setState((prev) => ({
          ...prev,
          enabled,
          lastCheckTime: storedLastCheck,
          initialized: true,
          shouldShowPopup: shouldShow
        }));
      } catch (error) {
        console.error('Error initializing mood tracking:', error);
        if (!ignore) {
          setState((prev) => ({ ...prev, initialized: true }));
        }
      }
    };

    void initialize();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!client) return;
    let ignore = false;

    const fetchConfig = async () => {
      const [error, config] = await client.stats.moodConfig.query();
      if (ignore || error || !config) return;
      setState((prev) => ({
        ...prev,
        dwellThresholdSeconds: config.dwellThresholdSeconds
      }));
    };

    void fetchConfig();

    return () => {
      ignore = true;
    };
  }, [client]);

  return (
    <MoodTrackingContext.Provider
      value={{
        moodTrackingEnabled: state.enabled,
        shouldShowMoodPopup: state.shouldShowPopup,
        lastCheckTime: state.lastCheckTime,
        initialized: state.initialized,
        enableMoodTracking,
        disableMoodTracking,
        hideMoodPopup,
        reportFactiiiEngagement,
        resetSession
      }}
    >
      {children}
    </MoodTrackingContext.Provider>
  );
};
